
import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";
import { TIERS } from "@/data/pricingTiers";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";

interface TierCardProps {
  tier: (typeof TIERS)[number];
}

const TierCard: React.FC<TierCardProps> = ({ tier }) => {
  const { startCheckout, loading } = useStripeCheckout();

  return (
    <Card className={`w-full ${tier.highlight ? "border-2 border-techne-purple shadow-lg" : ""}`}>
      <CardHeader className="flex flex-col items-start gap-2">
        <CardTitle className="text-xl">{tier.name}</CardTitle>
        <span className="text-sm text-muted-foreground">{tier.seats}</span>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="text-4xl font-bold leading-snug">{tier.price}</div>
        <p className="text-xs text-muted-foreground">{tier.perSeat}</p>
        <Button
          size="lg"
          disabled={loading}
          className="w-full mt-2 bg-techne-purple hover:bg-techne-deepPurple"
          onClick={() => startCheckout(tier.tierId)}
        >
          {loading ? "Redirecting…" : "Enroll Now"}
        </Button>
      </CardContent>
    </Card>
  );
};

interface BlueprintPricingProps {
  onEnrollClick?: () => void;
}

const BlueprintPricing: React.FC<BlueprintPricingProps> = () => {
  return (
    <section className="mb-16">
      <h2 className="text-3xl font-bold text-center mb-6">Pricing Options</h2>
      <p className="text-center text-sm text-muted-foreground mb-10">
        Choose the pricing tier that works best for your board
      </p>
      <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6">
        {TIERS.map((tier) => (
          <TierCard key={tier.id} tier={tier} />
        ))}
      </div>

      {/* Enterprise CTA */}
      <div className="mt-8 p-6 bg-gray-50 rounded-lg border border-gray-100 text-center">
        <h3 className="text-xl font-semibold mb-3">Enterprise Board Solutions</h3>
        <p className="text-gray-600 mb-4">
          Need licenses for a larger board (11+ seats) or custom enterprise solution?<br />
          Contact us for special pricing and implementation options.
        </p>
        <div className="flex items-center justify-center gap-2 text-techne-purple">
          <Mail className="h-5 w-5" />
          <a href="mailto:info@boardsight.ai" className="font-medium hover:underline">
            info@boardsight.ai
          </a>
        </div>
      </div>
    </section>
  );
};

export default BlueprintPricing;
